import React, { useState } from "react";
import {
  Box, Typography, Stack, Paper, TextField, Button, Chip,
  IconButton, Tooltip, Tabs, Tab, Alert, Select, MenuItem,
  FormControl, InputLabel, Grid,
} from "@mui/material";
import {
  Link as LinkIcon, Instagram, OpenInNew, Refresh,
  QuestionAnswer, Add, Hub, Campaign,
} from "@mui/icons-material";
import { DOMAIN, PAGE_LINKS, uid, CopyButton } from "./marketingShared";

// ─── Constants ───────────────────────────────────────────────────────────────

const BIO_TEMPLATES = [
  { tag: "Motivational", text: "Veterinary education, redefined.\nLearn. Grow. Inspire. 🐾\nJoin thousands of passionate vets on their journey.\nStart your path today ↓" },
  { tag: "CTA Focus",    text: "🎓 Online vet education platform\n📚 Premium courses by expert vets\n💪 Empower your veterinary career\n➡️ Link below to get started" },
  { tag: "Community",    text: "Building a community of dedicated vets.\n🐕 Learn together. Grow together.\n🌟 Courses, mentorship & more.\nJoin the Dr.Paw family ↓" },
  { tag: "Achievement",  text: "🏆 Level up your vet career\n🔥 Expert-led online courses\n🌍 Trusted by vets worldwide\nEnroll now — link in bio" },
];

const CAPTION_TEMPLATES = [
  { tag: "Engagement",    text: "Every great veterinarian started somewhere. 🐾\n\nThe journey of a thousand miles begins with one step — and yours starts with the right education.\n\nWe built Dr.Paw to give you exactly that.\n\nTap the link in our bio to explore our courses.\n\n#DrPaw #VetEducation #VeterinaryLife #LearningEveryDay" },
  { tag: "Motivational",  text: "Your patients trust you with their lives.\nMake sure you have the knowledge to honor that trust. 💚\n\nOur courses are designed by vets, for vets.\n\nStart learning today → link in bio.\n\n#VetLife #ContinuingEducation #DrPaw #VeterinaryMedicine" },
  { tag: "Course Launch", text: "🚨 New Course Alert! 🚨\n\nWe just dropped a brand new module on [TOPIC].\nEverything you need to know, broken down by experts.\n\nEnroll now and level up your skills.\nLink in bio! 🔗\n\n#DrPaw #NewCourse #VetEducation #OnlineLearning" },
  { tag: "Community",     text: "You asked, we listened. 👀\n\nOur community of [N]+ passionate vets is growing every day — and we want you in it.\n\nJoin us. Learn. Share. Inspire.\nLink in bio to get started. 🐾\n\n#VetCommunity #DrPaw #VeterinaryFamily" },
];

const QA_SUGGESTIONS = [
  { topic: "Dog Nutrition",         question: "What should dogs eat for optimal health?",                 path: "/membership/classes" },
  { topic: "Vaccination Protocols", question: "When should pets get their vaccinations?",                 path: "/membership/classes" },
  { topic: "Surgical Techniques",   question: "How are modern surgical techniques changing vet care?",    path: "/membership/classes" },
  { topic: "Animal Behavior",       question: "Why does my pet behave the way it does?",                  path: "/membership/classes" },
  { topic: "Emergency Care",        question: "What to do in a pet emergency?",                           path: "/membership/classes" },
  { topic: "Join Dr.Paw",           question: "How can I advance my veterinary career?",                  path: "/#pricing" },
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface GeneratedLink { label: string; url: string; }
interface QAItem { id: string; question: string; qaUrl: string; courseUrl: string; caption: string; }
interface UtmLink { id: string; label: string; url: string; }

// ─────────────────────────────────────────────────────────────────────────────

const AdminMarketingLinks: React.FC = () => {
  const [tab, setTab] = useState(0);

  // Tab 0: Link Generator
  const [customPath, setCustomPath]         = useState("");
  const [customLabel, setCustomLabel]       = useState("");
  const [generatedLinks, setGeneratedLinks] = useState<GeneratedLink[]>([]);
  const [linkAlert, setLinkAlert]           = useState("");

  // Tab 1: Instagram Bio
  const [bioText, setBioText]         = useState("");
  const [captionText, setCaptionText] = useState("");
  const [linkTarget, setLinkTarget]   = useState("/#pricing");
  const [bioMode, setBioMode]         = useState<"bio" | "caption">("bio");

  // Tab 2: Q&A Links
  const [qaQuestion, setQaQuestion] = useState("");
  const [qaPath, setQaPath]         = useState("/membership/classes");
  const [qaItems, setQaItems]       = useState<QAItem[]>([]);
  const [qaAlert, setQaAlert]       = useState("");

  // Tab 3: UTM Builder
  const [utmPath, setUtmPath]         = useState("/#pricing");
  const [utmSource, setUtmSource]     = useState("");
  const [utmMedium, setUtmMedium]     = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmTerm, setUtmTerm]         = useState("");
  const [utmContent, setUtmContent]   = useState("");
  const [utmLinks, setUtmLinks]       = useState<UtmLink[]>([]);
  const [utmAlert, setUtmAlert]       = useState("");

  // ── Handlers: Link Generator ──────────────────────────────────────────────
  const handleGenerateLink = () => {
    if (!customPath.trim()) { setLinkAlert("Enter a path first."); return; }
    const path  = customPath.startsWith("/") ? customPath : `/${customPath}`;
    const url   = `${DOMAIN}${path}`;
    const label = customLabel.trim() || path;
    setGeneratedLinks((prev) => [{ label, url }, ...prev.filter((l) => l.url !== url)]);
    setCustomPath(""); setCustomLabel(""); setLinkAlert("");
  };

  // ── Handlers: Q&A Links ───────────────────────────────────────────────────
  const buildQACaption = (question: string, qaUrl: string) =>
    `Got a question? 🙋‍♀️\n\n"${question}"\n\nShare your answer and discover more!\n👉 ${qaUrl}\n\n#DrPaw #VetEducation #AskDrPaw`;

  const handleAddQA = () => {
    if (!qaQuestion.trim()) { setQaAlert("Enter a question first."); return; }
    const path      = qaPath.startsWith("/") ? qaPath : `/${qaPath}`;
    const courseUrl = `${DOMAIN}${path}`;
    const qaUrl     = `${DOMAIN}/qa?q=${encodeURIComponent(qaQuestion.trim())}&link=${encodeURIComponent(courseUrl)}`;
    setQaItems((prev) => [{
      id: uid(), question: qaQuestion.trim(), qaUrl, courseUrl,
      caption: buildQACaption(qaQuestion.trim(), qaUrl),
    }, ...prev]);
    setQaQuestion(""); setQaAlert("");
  };

  const handleQASuggestion = (s: typeof QA_SUGGESTIONS[0]) => {
    const courseUrl = `${DOMAIN}${s.path}`;
    const qaUrl     = `${DOMAIN}/qa?q=${encodeURIComponent(s.question)}&link=${encodeURIComponent(courseUrl)}`;
    setQaItems((prev) => [{ id: uid(), question: s.question, qaUrl, courseUrl, caption: buildQACaption(s.question, qaUrl) }, ...prev]);
  };

  const buildBioLink = () => `${DOMAIN}${linkTarget}`;

  // ── Handlers: UTM Builder ─────────────────────────────────────────────────
  const buildUtmUrl = () => {
    const params = new URLSearchParams();
    if (utmSource.trim())   params.set("utm_source", utmSource.trim());
    if (utmMedium.trim())   params.set("utm_medium", utmMedium.trim());
    if (utmCampaign.trim()) params.set("utm_campaign", utmCampaign.trim());
    if (utmTerm.trim())     params.set("utm_term", utmTerm.trim());
    if (utmContent.trim())  params.set("utm_content", utmContent.trim());
    const query = params.toString();
    return `${DOMAIN}${utmPath}${query ? `?${query}` : ""}`;
  };

  const handleGenerateUtm = () => {
    if (!utmSource.trim() || !utmMedium.trim() || !utmCampaign.trim()) {
      setUtmAlert("Source, Medium, and Campaign are required for UTM tracking.");
      return;
    }
    const url   = buildUtmUrl();
    const label = `${utmSource.trim()} / ${utmMedium.trim()} / ${utmCampaign.trim()}`;
    setUtmLinks((prev) => [{ id: uid(), label, url }, ...prev]);
    setUtmAlert("");
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#0D47A1 0%,#1565C0 55%,#1976D2 100%)",
        boxShadow: "0 8px 32px rgba(21,101,192,.3)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Hub sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                Links & Content
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)", mt: 0.3 }}>
                Generate shareable links, craft Instagram bios & captions, and build Q&A engagement links
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {["Link Builder", "Bio & Captions", "Q&A Links", "UTM Builder"].map((t) => (
              <Chip key={t} label={t} size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: "1px solid #E8EDF5",
          "& .MuiTab-root": { fontWeight: 600, textTransform: "none", fontSize: ".9rem", minHeight: 48 },
          "& .Mui-selected": { color: "#1565C0" },
          "& .MuiTabs-indicator": { bgcolor: "#1565C0", height: 3, borderRadius: "3px 3px 0 0" },
        }}
      >
        <Tab icon={<LinkIcon fontSize="small" />}       iconPosition="start" label="Link Generator" />
        <Tab icon={<Instagram fontSize="small" />}      iconPosition="start" label="Bio & Captions" />
        <Tab icon={<QuestionAnswer fontSize="small" />} iconPosition="start" label="Q&A Links"      />
        <Tab icon={<Campaign fontSize="small" />}       iconPosition="start" label="UTM Builder"    />
      </Tabs>

      {/* ── TAB 0: Link Generator ──────────────────────────────────────────── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#0A1628" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Site Pages</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Ready-to-share links for drpawleb.com</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {PAGE_LINKS.map((pl) => {
                  const url = `${DOMAIN}${pl.path}`;
                  return (
                    <Box key={pl.path} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1, borderRadius: "10px", border: "1px solid #E8EDF5", "&:last-child": { mb: 0 } }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628" }}>{pl.label}</Typography>
                        <Typography variant="caption" sx={{ color: "#78909C" }}>{url}</Typography>
                      </Box>
                      <CopyButton text={url} />
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(url, "_blank")} sx={{ color: "#78909C", borderRadius: "8px" }}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#1565C0" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Custom Link Builder</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>Build a shareable link for any page path</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {linkAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLinkAlert("")}>{linkAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField
                    label="Page path"
                    placeholder="/courses/my-course-id"
                    value={customPath}
                    onChange={(e) => setCustomPath(e.target.value)}
                    size="small"
                    fullWidth
                    helperText={customPath ? `${DOMAIN}${customPath.startsWith("/") ? customPath : `/${customPath}`}` : ""}
                    InputProps={{ startAdornment: <Typography sx={{ color: "#78909C", fontSize: ".85rem", mr: 0.5 }}>{DOMAIN}</Typography> }}
                    onKeyDown={(e) => e.key === "Enter" && handleGenerateLink()}
                  />
                  <TextField
                    label="Label (optional)"
                    placeholder="e.g. Summer Campaign"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                    size="small"
                    fullWidth
                    onKeyDown={(e) => e.key === "Enter" && handleGenerateLink()}
                  />
                  <Button
                    variant="contained"
                    onClick={handleGenerateLink}
                    startIcon={<LinkIcon />}
                    sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                  >
                    Generate Link
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {generatedLinks.length > 0 && (
              <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0F7FF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Generated Links ({generatedLinks.length})</Typography>
                  <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => setGeneratedLinks([])} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                </Box>
                <Box sx={{ p: 2 }}>
                  {generatedLinks.map((gl, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1, borderRadius: "10px", bgcolor: "#F8FAFF", border: "1px solid #DDEEFF", "&:last-child": { mb: 0 } }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628" }}>{gl.label}</Typography>
                        <Typography variant="caption" sx={{ color: "#1565C0", wordBreak: "break-all" }}>{gl.url}</Typography>
                      </Box>
                      <CopyButton text={gl.url} />
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(gl.url, "_blank")} sx={{ color: "#78909C", borderRadius: "8px" }}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {/* ── TAB 1: Instagram Bio & Captions ──────────────────────────────── */}
      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip label="Bio Templates"     onClick={() => setBioMode("bio")}     color={bioMode === "bio"     ? "primary" : "default"} sx={{ fontWeight: 700, cursor: "pointer" }} />
              <Chip label="Caption Templates" onClick={() => setBioMode("caption")} color={bioMode === "caption" ? "primary" : "default"} sx={{ fontWeight: 700, cursor: "pointer" }} />
            </Stack>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#833AB4,#E1306C)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Instagram sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>{bioMode === "bio" ? "Bio Templates" : "Caption Templates"}</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Click any template to load it into the editor</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {(bioMode === "bio" ? BIO_TEMPLATES : CAPTION_TEMPLATES).map((t, i) => (
                  <Box
                    key={i}
                    onClick={() => bioMode === "bio" ? setBioText(t.text) : setCaptionText(t.text)}
                    sx={{ p: 2, mb: 1.5, borderRadius: "12px", border: "1px solid #E8EDF5", cursor: "pointer", transition: "all .2s", "&:hover": { borderColor: bioMode === "bio" ? "#E1306C" : "#833AB4", bgcolor: bioMode === "bio" ? "#FFF5F7" : "#FAF5FF" }, "&:last-child": { mb: 0 } }}
                  >
                    <Chip label={t.tag} size="small" sx={{ mb: 1, fontSize: ".7rem", fontWeight: 700, bgcolor: bioMode === "bio" ? "rgba(225,48,108,.1)" : "rgba(131,58,180,.1)", color: bioMode === "bio" ? "#E1306C" : "#833AB4" }} />
                    <Typography variant="body2" sx={{ color: "#455A64", whiteSpace: "pre-line", fontSize: ".83rem", lineHeight: 1.6 }}>{t.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#0A1628" }}>
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Instagram Bio Editor</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Edit and copy your bio text</Typography>
                </Box>
                <CopyButton text={bioText} />
              </Box>
              <Box sx={{ p: 2.5 }}>
                <TextField multiline rows={6} fullWidth value={bioText} onChange={(e) => setBioText(e.target.value)} placeholder="Select a bio template on the left, or write your own..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: "#78909C" }}>
                    {bioText.length} characters
                    {bioText.length > 150 && <Typography component="span" variant="caption" sx={{ color: "#EF4444", ml: 1 }}>(Instagram bio limit is ~150 chars)</Typography>}
                  </Typography>
                  <Button size="small" onClick={() => setBioText("")} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                </Stack>
              </Box>
            </Paper>

            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0F7FF" }}>
                <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Link-in-Bio Builder</Typography>
                <Typography variant="caption" sx={{ color: "#78909C" }}>Choose which page to send Instagram followers to</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Target Page</InputLabel>
                    <Select label="Target Page" value={linkTarget} onChange={(e) => setLinkTarget(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Box sx={{ flex: 1, px: 2, py: 1.2, borderRadius: "10px", bgcolor: "#EEF4FF", border: "1px solid #DDEEFF" }}>
                    <Typography variant="body2" sx={{ color: "#1565C0", fontWeight: 600, wordBreak: "break-all" }}>{buildBioLink()}</Typography>
                  </Box>
                  <CopyButton text={buildBioLink()} />
                </Stack>
              </Box>
            </Paper>

            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
              <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(135deg,#833AB4,#E1306C)" }}>
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Caption Editor</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>Write or refine your post caption</Typography>
                </Box>
                <CopyButton text={captionText} />
              </Box>
              <Box sx={{ p: 2.5 }}>
                <TextField multiline rows={8} fullWidth value={captionText} onChange={(e) => setCaptionText(e.target.value)} placeholder="Select a caption template on the left, or write your own..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ color: "#78909C" }}>{captionText.length} characters</Typography>
                  <Button size="small" onClick={() => setCaptionText("")} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 2: Q&A Links ─────────────────────────────────────────────── */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#0F766E" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <QuestionAnswer sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Suggested Q&A Topics</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Click to instantly generate a Q&A link</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {QA_SUGGESTIONS.map((s, i) => (
                  <Box key={i} onClick={() => handleQASuggestion(s)} sx={{ p: 1.5, mb: 1, borderRadius: "12px", border: "1px solid #E8EDF5", cursor: "pointer", transition: "all .2s", "&:hover": { borderColor: "#0F766E", bgcolor: "#F0FDF4" }, "&:last-child": { mb: 0 } }}>
                    <Typography sx={{ fontWeight: 700, fontSize: ".83rem", color: "#0A1628", mb: 0.3 }}>{s.topic}</Typography>
                    <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.4 }}>{s.question}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#0F766E" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Custom Q&A Link Builder</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>Create an engagement link with a question for your followers</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {qaAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setQaAlert("")}>{qaAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField
                    label="Your question for followers"
                    placeholder="e.g. Wondering how to treat parvovirus in dogs?"
                    value={qaQuestion}
                    onChange={(e) => setQaQuestion(e.target.value)}
                    size="small" fullWidth multiline rows={2}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleAddQA()}
                  />
                  <FormControl size="small" fullWidth>
                    <InputLabel>Link to Page</InputLabel>
                    <Select label="Link to Page" value={qaPath} onChange={(e) => setQaPath(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label} — {`${DOMAIN}${pl.path}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={handleAddQA} startIcon={<Add />} sx={{ bgcolor: "#0F766E", "&:hover": { bgcolor: "#0D6B63" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                    Generate Q&A Link
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {qaItems.length > 0 ? (
              <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Generated Q&A Links ({qaItems.length})</Typography>
                  <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => setQaItems([])} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear All</Button>
                </Box>
                <Box sx={{ p: 2 }}>
                  {qaItems.map((item) => (
                    <Box key={item.id} sx={{ p: 2, mb: 2, borderRadius: "12px", border: "1px solid #D1FAE5", bgcolor: "#F8FFFB", "&:last-child": { mb: 0 } }}>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628", mb: 0.5 }}>{item.question}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: "#0F766E", flex: 1, wordBreak: "break-all" }}>{item.qaUrl}</Typography>
                        <CopyButton text={item.qaUrl} small />
                        <Tooltip title="Open">
                          <IconButton size="small" onClick={() => window.open(item.qaUrl, "_blank")} sx={{ color: "#78909C", borderRadius: "6px" }}>
                            <OpenInNew sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <Box sx={{ bgcolor: "#E8F5E9", borderRadius: "8px", p: 1.5, position: "relative" }}>
                        <Typography variant="caption" sx={{ color: "#555", whiteSpace: "pre-line", lineHeight: 1.7, display: "block", pr: 4 }}>{item.caption}</Typography>
                        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                          <CopyButton text={item.caption} small />
                        </Box>
                      </Box>
                      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button size="small" color="error" onClick={() => setQaItems((p) => p.filter((x) => x.id !== item.id))} sx={{ textTransform: "none", fontSize: ".78rem" }}>Remove</Button>
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ) : (
              <Box sx={{ py: 6, textAlign: "center", color: "#78909C" }}>
                <QuestionAnswer sx={{ fontSize: 48, color: "#B2DFDB", mb: 1 }} />
                <Typography variant="body2">No Q&A links yet — use the builder above or click a suggestion.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {/* ── TAB 3: UTM Builder ───────────────────────────────────────────── */}
      {tab === 3 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#6A1B9A" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Campaign sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>UTM Campaign Link Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Tag links so GA4 attributes traffic to the right source, medium & campaign</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {utmAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUtmAlert("")}>{utmAlert}</Alert>}
                <Stack spacing={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Target Page</InputLabel>
                    <Select label="Target Page" value={utmPath} onChange={(e) => setUtmPath(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Source *" placeholder="e.g. instagram, newsletter, facebook" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} size="small" fullWidth />
                  <TextField label="Medium *" placeholder="e.g. social, email, cpc" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} size="small" fullWidth />
                  <TextField label="Campaign *" placeholder="e.g. summer_sale_2026" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} size="small" fullWidth />
                  <TextField label="Term (optional)" placeholder="e.g. vet_courses" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} size="small" fullWidth />
                  <TextField label="Content (optional)" placeholder="e.g. carousel_ad_1" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} size="small" fullWidth />
                  <Box sx={{ px: 2, py: 1.2, borderRadius: "10px", bgcolor: "#F3E5F5", border: "1px solid #E1BEE7" }}>
                    <Typography variant="caption" sx={{ color: "#6A1B9A", fontWeight: 600, wordBreak: "break-all" }}>{buildUtmUrl()}</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={handleGenerateUtm}
                    startIcon={<Campaign />}
                    sx={{ bgcolor: "#6A1B9A", "&:hover": { bgcolor: "#4A148C" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                  >
                    Generate UTM Link
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {utmLinks.length > 0 ? (
              <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2.5, py: 2, bgcolor: "#F3E5F5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>Generated UTM Links ({utmLinks.length})</Typography>
                  <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => setUtmLinks([])} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                </Box>
                <Box sx={{ p: 2 }}>
                  {utmLinks.map((ul) => (
                    <Box key={ul.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, mb: 1, borderRadius: "10px", bgcolor: "#FAF5FF", border: "1px solid #E1BEE7", "&:last-child": { mb: 0 } }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: "#0A1628" }}>{ul.label}</Typography>
                        <Typography variant="caption" sx={{ color: "#6A1B9A", wordBreak: "break-all" }}>{ul.url}</Typography>
                      </Box>
                      <CopyButton text={ul.url} small />
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => window.open(ul.url, "_blank")} sx={{ color: "#78909C", borderRadius: "8px" }}>
                          <OpenInNew fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ) : (
              <Box sx={{ py: 6, textAlign: "center", color: "#78909C" }}>
                <Campaign sx={{ fontSize: 48, color: "#E1BEE7", mb: 1 }} />
                <Typography variant="body2">No UTM links yet — fill in the builder and click Generate.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default AdminMarketingLinks;
